import { Mail } from 'lucide-react';
import arivitiLogo from '../assets/ariviti-logo-white.svg';
import { siteConfig } from '../config/siteConfig';
import { PageLink } from './PageLink';

interface FooterProps {
  onNavigate: (page: string) => void;
  onNavigateToDemo: () => void;
}

const linkClass =
  'inline-block py-1.5 text-sm font-normal text-indigo-tint-20 hover:text-white transition-colors';

// Circular chip for social + contact icons.
const iconCircle =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-royal-indigo bg-transparent text-indigo-tint-20 hover:bg-royal-indigo hover:text-white transition-colors';

const colHeading = 'font-display font-semibold text-sm uppercase tracking-wider text-white';

export function Footer({ onNavigate }: FooterProps) {
  return (
    <footer className="bg-dark-indigo text-indigo-tint-20 border-t border-royal-indigo">
      <div className="container mx-auto px-4">
        {/* Link columns */}
        <div className="grid grid-cols-1 gap-8 py-12 md:grid-cols-2 lg:grid-cols-[2.2fr_1fr_1fr_1fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-indigo-tint-20">TurfAI is built by</p>
              <a href={siteConfig.company.copyrightUrl} target="_blank" rel="noopener noreferrer" className="inline-block">
                <img src={arivitiLogo} alt="Ariviti" className="h-8 w-auto" />
              </a>
            </div>
            <p className="text-sm leading-relaxed text-indigo-tint-20">
              10th floor, RMZ Latitude Commercial Building,<br />
              Bellary Rd, Hebbal,<br />
              Bengaluru, Karnataka 560024, India
            </p>
            {/* ponytail: LinkedIn/X icons hidden until their styling/hover design lands; links stay in siteConfig.social. */}
          </div>

          <div className="space-y-4">
            <h3 className={colHeading}>Platform</h3>
            <ul className="space-y-0.5 text-sm">
              <li><PageLink page="platform" onNavigate={onNavigate} className={linkClass}>Platform overview</PageLink></li>
              <li><PageLink page="solutions" onNavigate={onNavigate} className={linkClass}>Solutions</PageLink></li>
              <li><PageLink page="resources" onNavigate={onNavigate} className={linkClass}>Resources</PageLink></li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className={colHeading}>Accelerators &amp; Tools</h3>
            <ul className="space-y-0.5 text-sm">
              <li><PageLink page="stageone" onNavigate={onNavigate} className={linkClass}>StageOne</PageLink></li>
              <li><PageLink page="learning" onNavigate={onNavigate} className={linkClass}>Learning tools</PageLink></li>
              <li><PageLink page="promptbench" onNavigate={onNavigate} className={linkClass}>PromptBench</PageLink></li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className={colHeading}>Contact</h3>
            <ul className="space-y-3 text-sm">
              {[siteConfig.contact.general, siteConfig.contact.support].map((email) => (
                <li key={email}>
                  <a href={`mailto:${email}`} className="group flex items-center gap-3">
                    <span className={iconCircle}>
                      <Mail className="w-4 h-4" />
                    </span>
                    <span className="text-indigo-tint-20 group-hover:text-white transition-colors">{email}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="h-px w-full bg-royal-indigo" />

        <div className="flex flex-col items-start justify-between gap-4 py-6 sm:flex-row sm:items-center">
          <p className="text-sm text-indigo-tint-20">
            © {siteConfig.company.copyrightYear}{' '}
            <a
              href={siteConfig.company.copyrightUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              {siteConfig.company.legalName}
            </a>
            . All rights reserved.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {[
              { label: 'Privacy Policy', page: 'privacy-policy' },
              { label: 'Terms of Service', page: 'terms-of-service' },
              { label: 'Security', page: 'security' },
            ].map((l) => (
              <PageLink key={l.page} page={l.page} onNavigate={onNavigate} className="inline-block py-1.5 text-sm text-indigo-tint-20 hover:text-white transition-colors">
                {l.label}
              </PageLink>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
